/**
 * Daily dockets (field.docket): the leading hand's docket step in the end of shift checklist (check the crew's
 * times, breaks, vehicles and equipment; preview the hours; the supervisor signs on this phone, or send a link),
 * and the office's docket panel for each shift (status, PDF, paper docket, corrected docket, links).
 */
import { useEffect, useRef, useState } from "react";
import { Icon } from "../../components/Icon";
import { Notice, useNotice } from "../../components/Notice";
import { Chip } from "../../components/States";
import { isNetworkError, isQueued, QUEUED_MESSAGE } from "../../lib/api";
import { localInstant, localYmd } from "../../lib/format";
import { openBlobInNewTab } from "../../lib/jobs-data";
import { useAsync } from "../../lib/useAsync";
import {
  cancelDocketLink,
  correctDocket,
  crewDocket,
  docketStates,
  DOCKET_STATE_LABEL,
  docketPdf,
  docketPhoto,
  officeDocket,
  previewDocket,
  recordPaperDocket,
  resendDocket,
  sendDocket,
  signDocketOnSite,
  unsignedYesterday,
  withdrawDocket,
  type DocketConfigView,
  type DocketDraft,
  type DocketPack,
  type DocketState,
  type DocketView,
  type SentLink,
} from "./docket-data";

const TZ = __OPS_PORTAL__.timezone;
const hmOf = (iso: string) => new Intl.DateTimeFormat("en-GB", { timeZone: TZ, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date(iso));
/** A time on the shift: on its start date, the next day when it falls well before the start (overnight). */
function onShift(shiftStart: string, hm: string, after?: string): string {
  let at = localInstant(localYmd(new Date(shiftStart)), hm);
  if (at.getTime() < Date.parse(shiftStart) - 3 * 3600_000) at = new Date(at.getTime() + 86400_000);
  if (after && at.getTime() <= Date.parse(after)) at = new Date(at.getTime() + 86400_000);
  return at.toISOString();
}

export const docketChip = (s: DocketState) => (
  <Chip tone={s === "signed" || s === "paper" || s === "corrected" ? "green" : s === "disputed" ? "primary" : s === "sent" ? "accent" : "primary-outline"}>{DOCKET_STATE_LABEL[s]}</Chip>
);

/* ------------------------------------------------------------------ pieces */

/** A drawn signature as SVG path data in a 600 x 160 box (the same format as the website's signing pages). */
function SignaturePad({ onChange }: { onChange: (path: string) => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const path = useRef("");
  const drawing = useRef(false);
  const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return [Math.round(((e.clientX - r.left) / r.width) * 6000) / 10, Math.round(((e.clientY - r.top) / r.height) * 1600) / 10] as const;
  };
  const ctx = () => {
    const c = ref.current!.getContext("2d")!;
    c.lineWidth = 3;
    c.lineCap = "round";
    c.lineJoin = "round";
    return c;
  };
  return (
    <div className="stack" style={{ gap: 6 }}>
      <canvas
        ref={ref}
        width={600}
        height={160}
        className="sigpad"
        aria-label="Signature pad"
        style={{ width: "100%", maxWidth: 600, aspectRatio: "600 / 160", border: "1px dashed var(--c-line-strong)", background: "var(--c-white)", touchAction: "none" }}
        onPointerDown={(e) => {
          drawing.current = true;
          e.currentTarget.setPointerCapture(e.pointerId);
          const [x, y] = point(e);
          path.current += `M${x} ${y} `;
          ctx().beginPath();
          ctx().moveTo(x, y);
        }}
        onPointerMove={(e) => {
          if (!drawing.current || path.current.length > 55000) return;
          const [x, y] = point(e);
          path.current += `L${x} ${y} `;
          const c = ctx();
          c.lineTo(x, y);
          c.stroke();
        }}
        onPointerUp={() => {
          drawing.current = false;
          onChange(path.current.trim());
        }}
        onPointerCancel={() => (drawing.current = false)}
      />
      <span>
        <button type="button" className="btn btn--outline btn--xs" onClick={() => { path.current = ""; ctx().clearRect(0, 0, 600, 160); onChange(""); }}>Clear</button>
      </span>
    </div>
  );
}

/** Crew times, breaks, vehicles, equipment and notes. */
export function DraftEditor({ draft, setDraft, config, shiftStart }: { draft: DocketDraft; setDraft: (d: DocketDraft) => void; config: DocketConfigView; shiftStart: string }) {
  const setPerson = (i: number, patch: Partial<DocketDraft["crew"][number]>) => setDraft({ ...draft, crew: draft.crew.map((p, j) => (j === i ? { ...p, ...patch } : p)) });
  const qty = (id: string) => draft.equipment.find((e) => e.id === id)?.quantity ?? 0;
  const setQty = (id: string, quantity: number) => setDraft({ ...draft, equipment: [...draft.equipment.filter((e) => e.id !== id), { id, quantity }] });
  return (
    <div className="stack" style={{ gap: 10 }}>
      {draft.crew.map((p, i) => (
        <div key={`${p.crewMemberId ?? p.name}-${i}`} className="card" style={{ padding: 10 }}>
          <div className="strong">{p.name}</div>
          <div className="timerow">
            <span>Start</span>
            <input className="input" type="time" aria-label={`Start time for ${p.name}`} value={hmOf(p.start)} onChange={(e) => e.target.value && setPerson(i, { start: onShift(shiftStart, e.target.value) })} />
          </div>
          <div className="timerow">
            <span>Finish</span>
            <input className="input" type="time" aria-label={`Finish time for ${p.name}`} value={hmOf(p.finish)} onChange={(e) => e.target.value && setPerson(i, { finish: onShift(shiftStart, e.target.value, p.start) })} />
          </div>
          <div className="timerow">
            <span>Breaks (minutes)</span>
            <input className="input" type="number" min={0} max={600} step={5} aria-label={`Breaks for ${p.name}`} value={p.breakMinutes} onChange={(e) => setPerson(i, { breakMinutes: Math.max(0, Number(e.target.value) || 0) })} />
          </div>
          {draft.crew.length > 1 && <button type="button" className="btn btn--outline btn--xs" onClick={() => setDraft({ ...draft, crew: draft.crew.filter((_, j) => j !== i) })}>Not on site today</button>}
        </div>
      ))}
      <div className="timerow">
        <span>{config.vehiclesLabel}</span>
        <input className="input" type="number" min={0} max={20} value={draft.vehicles} onChange={(e) => setDraft({ ...draft, vehicles: Math.max(0, Number(e.target.value) || 0) })} />
      </div>
      {config.equipment.map((e) => (
        <div key={e.id} className="timerow">
          <span>{e.label}{e.unit === "hour" ? " (hours)" : ""}</span>
          <input className="input" type="number" min={0} max={1000} step={e.unit === "hour" ? 0.25 : 1} value={qty(e.id)} onChange={(ev) => setQty(e.id, Math.max(0, Number(ev.target.value) || 0))} />
        </div>
      ))}
      <label className="field">
        <span className="field__label">Notes <span className="muted">(optional)</span></span>
        <textarea className="input" rows={2} value={draft.notes ?? ""} onChange={(e) => setDraft({ ...draft, notes: e.target.value || null })} />
      </label>
    </div>
  );
}

export function DocketHours({ view }: { view: DocketView }) {
  return (
    <div className="stack" style={{ gap: 8 }}>
      <ul className="list">
        {view.crew.map((p) => (
          <li key={p.name} className="list__row">
            <span>
              <strong>{p.name}</strong>
              <span className="sub">{p.start} to {p.finish}, breaks {p.breaks.toLowerCase()}. Worked {p.worked}, charged {p.charged}.</span>
              <span className="sub">{p.bands}{p.notes ? `. ${p.notes.charAt(0).toUpperCase()}${p.notes.slice(1)}` : ""}</span>
            </span>
          </li>
        ))}
      </ul>
      <p className="small">
        <strong>Totals:</strong> {view.totals.map((t) => `${t.band} ${t.hours} (${t.people})`).join(", ")}. {view.vehicles}{view.equipment.length ? `. ${view.equipment.join(". ")}` : ""}.
      </p>
      {view.notes && <p className="small">Notes: {view.notes}</p>}
    </div>
  );
}

const currentPosition = () =>
  new Promise<{ lat: number; lng: number; accuracyM: number | null } | null>((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition((p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracyM: p.coords.accuracy ?? null }), () => resolve(null), { timeout: 6000, maximumAge: 300000 });
  });

const smsHref = (sms: { to: string; body: string }) => `sms:${sms.to}${/iPhone|iPad|Mac/.test(navigator.userAgent) ? "&" : "?"}body=${encodeURIComponent(sms.body)}`;

function SentPanel({ sent }: { sent: SentLink }) {
  return (
    <div className="stack" style={{ gap: 6 }}>
      <p className="small">{sent.emailed ? "Emailed the link." : sent.emailError ? `The email failed: ${sent.emailError}` : "Not emailed."} The link works for 7 days and only once.</p>
      {sent.sms && <a className="btn btn--ink btn--block" href={smsHref(sent.sms)}><Icon name="external" size={16} /> Text the link</a>}
      <button type="button" className="btn btn--outline btn--sm" onClick={() => void navigator.clipboard?.writeText(sent.url)}>Copy the link</button>
    </div>
  );
}

/* ------------------------------------------------------------------ leading hand */

/** The docket item of the end of shift checklist. `finishTimes` are the checklist's answers so far. */
export function DocketItem({ bookingId, shiftStart, finishTimes, onChanged }: { bookingId: string; shiftStart: string; finishTimes: Record<string, string>; onChanged: () => void }) {
  const pack = useAsync(() => crewDocket(bookingId), [bookingId]);
  if (pack.loading && !pack.data) return <p className="small muted">Loading the docket</p>;
  if (pack.error) return <p className="small sub--primary">{pack.error}</p>;
  return <DocketFlow pack={pack.data!} bookingId={bookingId} shiftStart={shiftStart} finishTimes={finishTimes} onChanged={() => { pack.reload(); onChanged(); }} />;
}

function DocketFlow({ pack, bookingId, shiftStart, finishTimes, onChanged }: { pack: DocketPack; bookingId: string; shiftStart: string; finishTimes: Record<string, string>; onChanged: () => void }) {
  const inForce = [...pack.dockets].reverse().find((d) => d.status !== "cancelled" && !d.supersededBy) ?? null;
  const [draft, setDraft] = useState<DocketDraft>(() => ({ ...pack.prefill, crew: pack.prefill.crew.map((p) => (p.crewMemberId && finishTimes[p.crewMemberId] ? { ...p, finish: finishTimes[p.crewMemberId]! } : p)) }));
  const [view, setView] = useState<DocketView | null>(null);
  const [mode, setMode] = useState<"edit" | "sign" | "send">("edit");
  const [noSignal, setNoSignal] = useState(false);
  const [savedToSend, setSavedToSend] = useState(false);
  const [signed, setSigned] = useState<{ number: string; status: string } | null>(null);
  const [signer, setSigner] = useState({ name: "", position: "", company: "", email: "", typed: "", drawn: "", confirmed: false, disputing: false, dispute: "" });
  const [to, setTo] = useState({ name: "", email: "", phone: "" });
  const [sent, setSent] = useState<SentLink | null>(null);
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail, ok } = useNotice();

  useEffect(() => setView(null), [draft]);

  if (signed && !(inForce && inForce.number === signed.number)) {
    return (
      <div className="stack" style={{ gap: 6 }}>
        <Notice notice={notice} onDismiss={() => setNotice(null)} />
        <span>{docketChip(signed.status === "disputed" ? "disputed" : "signed")} <strong>{signed.number}</strong></span>
      </div>
    );
  }

  if (inForce && inForce.status !== "awaiting_signature") {
    return (
      <div className="stack" style={{ gap: 6 }}>
        <span>{docketChip(inForce.status === "disputed" ? "disputed" : inForce.status === "paper" ? "paper" : inForce.status === "corrected" ? "corrected" : "signed")} <strong>{inForce.number}</strong></span>
        <span className="small">Signed by {inForce.signerName}{inForce.signerCompany ? `, ${inForce.signerCompany}` : ""}, {inForce.signedAtLocal}.{inForce.disputeNote ? ` Dispute: ${inForce.disputeNote}` : ""}</span>
      </div>
    );
  }

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setNotice(null);
    try {
      await fn();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  if (inForce && !sent) {
    const link = inForce.links[0];
    return (
      <div className="stack" style={{ gap: 6 }}>
        <Notice notice={notice} onDismiss={() => setNotice(null)} />
        <span>{docketChip("sent")} <strong>{inForce.number}</strong></span>
        <span className="small">Waiting for {link?.recipientName ?? "the client"} to sign by link{link?.recipientEmail ? ` (${link.recipientEmail})` : link?.recipientPhone ? ` (${link.recipientPhone})` : ""}. The checklist can be submitted now.</span>
        <button type="button" className="btn btn--outline btn--sm" disabled={busy} onClick={() => void run(async () => { await withdrawDocket(bookingId, inForce.id); ok("Withdrawn. The link no longer works."); onChanged(); })}>
          The supervisor is here now: sign on this phone instead
        </button>
      </div>
    );
  }

  if (savedToSend) {
    return <p className="notice notice--ok" role="status"><Icon name="check" size={18} />{QUEUED_MESSAGE} The supervisor gets the link then. You can submit the checklist now.</p>;
  }

  const sendForm = (
                <div className="stack" style={{ gap: 8 }}>
                  {(["name", "email", "phone"] as const).map((k) => (
                    <label key={k} className="field">
                      <span className="field__label">{k === "name" ? "Supervisor's name" : k === "email" ? "Email" : "Mobile"}</span>
                      <input className="input input--lg" type={k === "email" ? "email" : k === "phone" ? "tel" : "text"} value={to[k]} onChange={(e) => setTo({ ...to, [k]: e.target.value })} />
                    </label>
                  ))}
                  <button
                    type="button"
                    className="btn btn--primary btn--block"
                    disabled={busy}
                    aria-busy={busy}
                    onClick={() => void run(async () => {
                      try {
                        setSent(await sendDocket(bookingId, draft, { name: to.name, email: to.email || null, phone: to.phone || null }));
                        onChanged();
                      } catch (e) {
                        if (!isQueued(e)) throw e;
                        setSavedToSend(true);
                      }
                    })}
                  >
                    Send the docket
                  </button>
                  <button type="button" className="btn btn--outline btn--block" disabled={busy} onClick={() => setMode("edit")}>Back</button>
                </div>
  );

  return (
    <div className="stack" style={{ gap: 10 }}>
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      {sent ? (
        <SentPanel sent={sent} />
      ) : (
        <>
          <DraftEditor draft={draft} setDraft={setDraft} config={pack.config} shiftStart={shiftStart} />
          {!view && !noSignal && (
            <button type="button" className="btn btn--ink btn--block" disabled={busy} aria-busy={busy} onClick={() => void run(async () => {
              try {
                setView(await previewDocket(bookingId, draft));
              } catch (e) {
                // No signal: the hours are worked out by the server, so send it for sign-off; it goes when back online.
                if (isNetworkError(e)) setNoSignal(true);
                else throw e;
              }
            })}>Work out the hours</button>
          )}
          {!view && noSignal && (
            <>
              <p className="notice notice--error" role="status"><Icon name="warning" size={18} />No signal, so the hours can't be worked out on this phone. Send the docket for sign-off: it is sent when you're back online, and the supervisor checks the hours when they sign. Or use a paper docket.</p>
              {sendForm}
              <button type="button" className="btn btn--outline btn--block" disabled={busy} onClick={() => setNoSignal(false)}>Try again</button>
            </>
          )}
          {view && (
            <>
              <DocketHours view={view} />
              {mode === "edit" && (
                <div className="stack" style={{ gap: 6 }}>
                  <button type="button" className="btn btn--primary btn--block" onClick={() => setMode("sign")}>Supervisor signs on this phone</button>
                  <button type="button" className="btn btn--outline btn--block" onClick={() => setMode("send")}>Supervisor not here: send for sign-off</button>
                </div>
              )}
              {mode === "sign" && (
                <div className="stack" style={{ gap: 8 }}>
                  <p className="small">Hand the phone to the client's supervisor.</p>
                  <label className="check"><input type="checkbox" checked={signer.confirmed} onChange={(e) => setSigner({ ...signer, confirmed: e.target.checked })} /> {pack.config.confirmStatement}</label>
                  {(["name", "position", "company", "email"] as const).map((k) => (
                    <label key={k} className="field">
                      <span className="field__label">{k === "name" ? "Full name" : k === "position" ? "Position" : k === "company" ? "Company" : "Email for their copy (optional)"}</span>
                      <input className="input input--lg" type={k === "email" ? "email" : "text"} value={signer[k]} onChange={(e) => setSigner({ ...signer, [k]: e.target.value })} />
                    </label>
                  ))}
                  <span className="field__label">Sign here</span>
                  <SignaturePad onChange={(drawn) => setSigner((s) => ({ ...s, drawn }))} />
                  <label className="field"><span className="field__label">Or type their full name as the signature</span><input className="input input--lg signature" value={signer.typed} onChange={(e) => setSigner({ ...signer, typed: e.target.value })} /></label>
                  <label className="check"><input type="checkbox" checked={signer.disputing} onChange={(e) => setSigner({ ...signer, disputing: e.target.checked })} /> They don't agree with the hours or crew</label>
                  {signer.disputing && <label className="field"><span className="field__label">What they dispute</span><textarea className="input" rows={2} value={signer.dispute} onChange={(e) => setSigner({ ...signer, dispute: e.target.value })} placeholder="e.g. Disputed: we say 6 hours" /></label>}
                  <button
                    type="button"
                    className="btn btn--primary btn--block"
                    disabled={busy || !signer.confirmed}
                    aria-busy={busy}
                    onClick={() =>
                      void run(async () => {
                        const r = await signDocketOnSite(bookingId, draft, {
                          name: signer.name, position: signer.position, company: signer.company, email: signer.email || null,
                          signatureTyped: signer.typed || null, signatureDrawn: signer.drawn || null, confirmed: true,
                          dispute: signer.disputing ? signer.dispute : null, gps: await currentPosition(),
                        });
                        ok(`Docket ${r.docket.number} signed${r.docket.status === "disputed" ? " with a dispute. The office has been told" : ""}.`);
                        // Leave the signing form at once, so it can't be signed twice while the docket reloads.
                        setSigned({ number: r.docket.number, status: r.docket.status });
                        setMode("edit");
                        onChanged();
                      })
                    }
                  >
                    Sign the docket
                  </button>
                  <button type="button" className="btn btn--outline btn--block" disabled={busy} onClick={() => setMode("edit")}>Back</button>
                </div>
              )}
              {mode === "send" && sendForm}
            </>
          )}
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ office */

/** The office's docket panel for one shift. */
export function OfficeDocket({ bookingId, shiftStart, onChanged }: { bookingId: string; shiftStart: string; onChanged: () => void }) {
  const pack = useAsync(() => officeDocket(bookingId), [bookingId]);
  const [form, setForm] = useState<null | "paper" | "correct" | "send">(null);
  const [draft, setDraft] = useState<DocketDraft | null>(null);
  const [paper, setPaper] = useState({ signerName: "", signerCompany: "", signedOn: localYmd(new Date(shiftStart)), disputeNote: "", photo: null as File | null });
  const [correction, setCorrection] = useState({ reason: "", issuerName: "" });
  const [to, setTo] = useState({ name: "", email: "", phone: "" });
  const [sent, setSent] = useState<SentLink | null>(null);
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail, ok } = useNotice();

  if (pack.loading && !pack.data) return <p className="small muted">Loading</p>;
  if (pack.error) return <p className="small sub--primary">{pack.error}</p>;
  const p = pack.data!;
  const inForce = [...p.dockets].reverse().find((d) => d.status !== "cancelled" && !d.supersededBy) ?? null;
  const open = (which: "paper" | "correct" | "send") => {
    setNotice(null);
    setSent(null);
    setDraft(p.prefill);
    setForm(which);
  };
  async function run(fn: () => Promise<string | void>) {
    setBusy(true);
    try {
      const msg = await fn();
      if (msg) ok(msg);
      setForm(null);
      pack.reload();
      onChanged();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack" style={{ gap: 8 }}>
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <span>{docketChip(p.state)} {inForce && <strong>{inForce.number}</strong>}</span>
      {inForce?.signerName && <span className="small">Signed by {inForce.signerName}{inForce.signerCompany ? `, ${inForce.signerCompany}` : ""}, {inForce.signedAtLocal}</span>}
      {inForce?.disputeNote && <span className="small sub--primary">Dispute: {inForce.disputeNote}</span>}
      {inForce?.status === "awaiting_signature" && inForce.links[0] && <span className="small">Link sent to {inForce.links[0].recipientName}, works until {new Date(inForce.links[0].expiresAt).toLocaleDateString("en-AU")}</span>}
      <span className="actions">
        {inForce && inForce.status !== "awaiting_signature" && <button type="button" className="btn btn--outline btn--xs" onClick={() => void openBlobInNewTab(() => docketPdf(inForce.id)).catch(fail)}>Docket PDF</button>}
        {inForce?.status === "paper" && <button type="button" className="btn btn--outline btn--xs" onClick={() => void openBlobInNewTab(() => docketPhoto(inForce.id)).catch(fail)}>Paper photo</button>}
        {inForce && inForce.status !== "awaiting_signature" && <button type="button" className="btn btn--outline btn--xs" onClick={() => open("correct")}>Issue a corrected docket</button>}
        {(!inForce || inForce.status === "awaiting_signature") && <button type="button" className="btn btn--outline btn--xs" onClick={() => open("paper")}>Signed on paper</button>}
        {inForce?.status === "awaiting_signature" && <button type="button" className="btn btn--outline btn--xs" onClick={() => open("send")}>Send the link again</button>}
        {inForce?.status === "awaiting_signature" && inForce.links[0] && (
          <button type="button" className="btn btn--outline btn--xs" disabled={busy} onClick={() => void run(async () => { await cancelDocketLink(inForce.links[0]!.id); return "Link cancelled."; })}>Cancel the link</button>
        )}
      </span>
      {p.dockets.filter((d) => d.supersededBy).map((d) => (
        <span key={d.id} className="small muted">
          {d.number} ({DOCKET_STATE_LABEL[d.status === "awaiting_signature" ? "sent" : (d.status as DocketState)] ?? d.status}), corrected{" "}
          <button type="button" className="btn btn--outline btn--xs" onClick={() => void openBlobInNewTab(() => docketPdf(d.id)).catch(fail)}>PDF</button>
        </span>
      ))}
      {form && draft && form !== "send" && (
        <div className="stack" style={{ gap: 8 }}>
          <DraftEditor draft={draft} setDraft={setDraft} config={p.config} shiftStart={shiftStart} />
          {form === "paper" ? (
            <>
              <label className="field"><span className="field__label">Who signed the paper docket</span><input className="input" value={paper.signerName} onChange={(e) => setPaper({ ...paper, signerName: e.target.value })} /></label>
              <label className="field"><span className="field__label">Their company</span><input className="input" value={paper.signerCompany} onChange={(e) => setPaper({ ...paper, signerCompany: e.target.value })} /></label>
              <label className="field"><span className="field__label">Date signed</span><input className="input" type="date" value={paper.signedOn} onChange={(e) => setPaper({ ...paper, signedOn: e.target.value })} /></label>
              <label className="field"><span className="field__label">Dispute written on it <span className="muted">(if any)</span></span><input className="input" value={paper.disputeNote} onChange={(e) => setPaper({ ...paper, disputeNote: e.target.value })} /></label>
              <label className="field"><span className="field__label">Photo of the signed docket</span><input className="input" type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={(e) => setPaper({ ...paper, photo: e.target.files?.[0] ?? null })} style={{ paddingTop: 9 }} /></label>
              <span className="actions">
                <button type="button" className="btn btn--primary btn--sm" disabled={busy || !paper.photo} aria-busy={busy} onClick={() => void run(async () => `Paper docket ${(await recordPaperDocket(bookingId, draft, { ...paper, photo: paper.photo! })).docket.number} recorded.`)}>Record the paper docket</button>
                <button type="button" className="btn btn--outline btn--sm" onClick={() => setForm(null)}>Cancel</button>
              </span>
            </>
          ) : (
            <>
              <label className="field"><span className="field__label">What the correction changes and why</span><textarea className="input" rows={2} value={correction.reason} onChange={(e) => setCorrection({ ...correction, reason: e.target.value })} placeholder="e.g. Agreed 6 hours with the supervisor by phone" /></label>
              <label className="field"><span className="field__label">Your full name</span><input className="input" value={correction.issuerName} onChange={(e) => setCorrection({ ...correction, issuerName: e.target.value })} /></label>
              <span className="actions">
                <button type="button" className="btn btn--primary btn--sm" disabled={busy} aria-busy={busy} onClick={() => void run(async () => `Corrected docket ${(await correctDocket(inForce!.id, draft, correction.reason, correction.issuerName)).docket.number} issued. ${inForce!.number} is kept on file.`)}>Issue the corrected docket</button>
                <button type="button" className="btn btn--outline btn--sm" onClick={() => setForm(null)}>Cancel</button>
              </span>
            </>
          )}
        </div>
      )}
      {form === "send" && inForce && (
        <div className="stack" style={{ gap: 6 }}>
          {sent ? (
            <SentPanel sent={sent} />
          ) : (
            <>
              <input className="input" aria-label="Name" placeholder="Name" value={to.name} onChange={(e) => setTo({ ...to, name: e.target.value })} />
              <input className="input" aria-label="Email" type="email" placeholder="Email" value={to.email} onChange={(e) => setTo({ ...to, email: e.target.value })} />
              <input className="input" aria-label="Mobile" type="tel" placeholder="Mobile" value={to.phone} onChange={(e) => setTo({ ...to, phone: e.target.value })} />
              <span className="actions">
                <button type="button" className="btn btn--primary btn--xs" disabled={busy} onClick={() => void (async () => { setBusy(true); try { setSent(await resendDocket(inForce.id, { name: to.name, email: to.email || null, phone: to.phone || null })); pack.reload(); } catch (e) { fail(e); } finally { setBusy(false); } })()}>Send</button>
                <button type="button" className="btn btn--outline btn--xs" onClick={() => setForm(null)}>Cancel</button>
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ the job's dockets */

/** Every shift on the job with its docket state; open one for its docket panel. */
export function JobDockets({ job, onChanged }: { job: { id: string; bookings: { id: string; starts_at: string; status: string }[] }; onChanged: () => void }) {
  const states = useAsync(() => docketStates(job.id), [job.id]);
  const [open, setOpen] = useState<string | null>(null);
  const shifts = job.bookings.filter((b) => b.status !== "cancelled").sort((a, b) => a.starts_at.localeCompare(b.starts_at));
  if (!shifts.length) return null;
  const fmt = (iso: string) => new Intl.DateTimeFormat("en-AU", { timeZone: TZ, weekday: "short", day: "numeric", month: "short" }).format(new Date(iso));
  return (
    <div className="card">
      <div className="card__head"><h2 className="card__title">Dockets</h2></div>
      <div className="card__body">
        <ul className="list">
          {shifts.map((b) => {
            const s = states.data?.[b.id];
            return (
              <li key={b.id} className="list__row" style={{ flexWrap: "wrap" }}>
                <span>{fmt(b.starts_at)} {hmOf(b.starts_at)}{s?.number ? <span className="sub">{s.number}{s.signer ? `, ${s.signer}` : ""}</span> : null}</span>
                <span className="actions">
                  {docketChip(s?.state ?? "not_started")}
                  <button type="button" className="btn btn--outline btn--xs" onClick={() => setOpen(open === b.id ? null : b.id)}>{open === b.id ? "Close" : "Open"}</button>
                </span>
                {open === b.id && (
                  <div style={{ flexBasis: "100%", marginTop: 8 }}>
                    <OfficeDocket bookingId={b.id} shiftStart={b.starts_at} onChanged={() => { states.reload(); onChanged(); }} />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

/** Whether the client layer has dockets (a docket item in the end of shift checklist). */
export const docketsInUse = () => Boolean(__OPS_PORTAL__.field?.checklists.end.some((i) => i.kind === "docket"));

/** Today: yesterday's shifts with no signed docket (not started, or sent and not signed yet). */
export function UnsignedDocketsCard() {
  const today = localYmd();
  const y = new Date(`${today}T12:00:00Z`);
  y.setUTCDate(y.getUTCDate() - 1);
  const s = useAsync(() => unsignedYesterday(today, y.toISOString().slice(0, 10)), [today]);
  const rows = s.data;
  return (
    <div className="card card--alert">
      <div className="card__head"><h2 className="card__title">Dockets not signed</h2></div>
      {s.loading && !rows && <p className="small muted" style={{ margin: "0 16px 12px" }}>Loading</p>}
      {s.error && <p className="small sub--primary" style={{ margin: "0 16px 12px" }}>{s.error}</p>}
      {rows && rows.length === 0 && <p className="small muted" style={{ margin: "0 16px 12px" }}>Every shift yesterday has a signed docket.</p>}
      {rows && rows.length > 0 && (
        <ul className="list">
          {rows.map((r) => (
            <li key={r.bookingId} className="list__row">
              <span>
                {r.jobId ? <a className="strong" href={`#/jobs/${r.jobId}`}>{r.reference} {r.title}</a> : <strong>Shift with no job</strong>}
                <span className="sub">Yesterday {hmOf(r.startsAt)}</span>
              </span>
              <span className="list__value">{docketChip(r.state)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
